import {
  Body,
  Box,
  CheckboxField,
  ComputedFieldProps,
  Divider,
  FormRow,
  Space,
  unsafeLocalizedString,
  useIsMobileLayout,
  useUniqueID,
} from "@dbn/omnichannel-design-system-g4p";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { option } from "fp-ts";
import { constant, constNull, pipe } from "fp-ts/function";
import {
  LocaleKey,
  RuntimeLocaleKey,
  useFormatDate,
  useFormatMessage,
} from "../../intl";
import { DisplayMoneyAmount } from "../../Common/Price/DisplayMoneyAmount";
import { getTypeOfProductLabel, LiabilityFormModel } from "../domain";
import * as classes from "../../Common/Price/DisplayMoneyAmount.css";
import { LoanOrigin } from "./RefinancingAccountSelection";
import { InfoTooltip } from "../../Common/InfoTooltip/InfoTooltip";

type Props = {
  namePrefix: string;
  onChangeValues: (value: LiabilityFormModel) => void;
  fieldProps: <K extends keyof LiabilityFormModel>(
    name: K
  ) => ComputedFieldProps<LiabilityFormModel[K]>;
  divider: boolean;
  showRepaidLoans: boolean;
  loanOrigin: LoanOrigin;
};

export function LiabilityEditableItem(props: Props) {
  const isMobile = useIsMobileLayout();
  const formatMessage = useFormatMessage();
  const formatDate = useFormatDate("dd-mm-yyyy");

  const labelId = useUniqueID();
  const startDateField = pipe(
    props.fieldProps("startDate").value,
    option.fold(constNull, startDate => (
      <Box className={classes.amount["x-small"]} vAlignContent="bottom">
        {pipe(
          option.some(startDate),
          option.fold(constant("-"), formatDate),
          unsafeLocalizedString
        )}
      </Box>
    ))
  );

  function getFieldHeader(textKey: LocaleKey) {
    return (
      <Body
        weight="regular"
        size="small"
        color={palette.dark}
        className={!isMobile ? classes.flexDisplay : undefined}
      >
        {formatMessage(textKey)}
      </Body>
    );
  }

  return (
    <Box column grow shrink>
      <Box
        column={isMobile}
        grow
        shrink
        style={
          !isMobile
            ? {
                justifyContent: "space-between",
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
              }
            : undefined
        }
      >
        <Box
          column
          shrink
          className={!isMobile ? classes.firstColumn : undefined}
        >
          {getFieldHeader(
            `StandardLoan.CustomerOffer.LiabilityItem.${props.loanOrigin}.typeOfProductLabel` as RuntimeLocaleKey
          )}
          {isMobile && <Space units={2} />}
          <CheckboxField
            {...props.fieldProps("selected")}
            label={formatMessage(
              getTypeOfProductLabel(props.fieldProps("type").value)
            )}
            onChange={selected =>
              props.onChangeValues({
                type: props.fieldProps("type").value,
                originalAmount: props.fieldProps("originalAmount").value,
                remainingAmount: props.fieldProps("remainingAmount").value,
                startDate: props.fieldProps("startDate").value,
                selected,
                recordId: props.fieldProps("recordId").value,
                selectedForRepaid:
                  props.fieldProps("selectedForRepaid").value && !selected,
              })
            }
          />
        </Box>
        {isMobile && <Space units={6} />}
        <Box column shrink>
          {getFieldHeader("StandardLoan.LiabilityItem.originalAmountLabel")}
          <Space units={2} />
          <DisplayMoneyAmount
            id={`${labelId}_original`}
            amount={props.fieldProps("originalAmount").value}
            size="x-small"
          />
        </Box>
        {isMobile && <Space units={6} />}
        <Box column shrink>
          {getFieldHeader("StandardLoan.LiabilityItem.remainingAmountLabel")}
          <Space units={2} />
          <DisplayMoneyAmount
            id={`${labelId}_remaining`}
            amount={props.fieldProps("remainingAmount").value}
            size="x-small"
          />
        </Box>
        {isMobile && <Space units={6} />}
        <Box column shrink>
          {getFieldHeader("StandardLoan.LiabilityItem.startDateLabel")}
          <Space units={2} />
          {startDateField}
        </Box>
        {props.showRepaidLoans && (
          <>
            {isMobile && <Space units={6} />}
            {isMobile ? (
              <CheckboxField
                {...props.fieldProps("selectedForRepaid")}
                label={formatMessage(
                  "StandardLoan.LiabilityItem.selectedForRepaidLabel"
                )}
                description={formatMessage(
                  "StandardLoan.LiabilityItem.selectedForRepaidLabel.description"
                )}
                descriptionAriaLabel={formatMessage(
                  "generic.tooltip.infoIcon.ariaLabel"
                )}
                tooltipCloseIconAriaLabel={formatMessage(
                  "generic.tooltip.closeIcon.ariaLabel"
                )}
                onChange={selectedForRepaid =>
                  props.onChangeValues({
                    type: props.fieldProps("type").value,
                    originalAmount: props.fieldProps("originalAmount").value,
                    remainingAmount: props.fieldProps("remainingAmount").value,
                    startDate: props.fieldProps("startDate").value,
                    selected:
                      props.fieldProps("selected").value && !selectedForRepaid,
                    recordId: props.fieldProps("recordId").value,
                    selectedForRepaid,
                  })
                }
              />
            ) : (
              <Box column vAlignContent="top" hAlignContent="center" shrink>
                <Box>
                  <span style={{ marginRight: "4px" }}>
                    {getFieldHeader(
                      "StandardLoan.LiabilityItem.selectedForRepaidLabel"
                    )}
                  </span>
                  <InfoTooltip
                    content={formatMessage(
                      "StandardLoan.LiabilityItem.selectedForRepaidLabel.description"
                    )}
                    size="small"
                    inline
                  />
                </Box>

                {isMobile && <Space units={2} />}
                <CheckboxField
                  {...props.fieldProps("selectedForRepaid")}
                  label={unsafeLocalizedString("")}
                  onChange={selectedForRepaid =>
                    props.onChangeValues({
                      type: props.fieldProps("type").value,
                      originalAmount: props.fieldProps("originalAmount").value,
                      remainingAmount: props.fieldProps("remainingAmount")
                        .value,
                      startDate: props.fieldProps("startDate").value,
                      selected:
                        props.fieldProps("selected").value &&
                        !selectedForRepaid,
                      recordId: props.fieldProps("recordId").value,
                      selectedForRepaid,
                    })
                  }
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Space units={4} />
      {props.divider && (
        <FormRow type="full">
          <Divider />
        </FormRow>
      )}
    </Box>
  );
}
